import React, { useRef, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Skeleton,
  Alert,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTableCell } from '@/components/shared/SortableTableCell';
import { usePersonLabels } from '@/hooks/usePersonLabels';

interface EntityActivity {
  id: string;
  audit_timestamp: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  entity_id: string | null;
  entity_type: string | null;
  entity_name: string | null;
  entity_identifier: string | null;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  change_summary: string;
  changed_fields: string[] | null;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  from_location_id: string | null;
  to_location_id: string | null;
  from_location_name: string | null;
  to_location_name: string | null;
  receipt_quantity: number | null;
}

interface EntityActivityListProps {
  entityType?: string;
  pollingInterval?: number;
  pageSize?: number;
}

const getOperationColor = (
  operation: EntityActivity['operation'],
): 'success' | 'error' | 'info' | 'default' => {
  switch (operation) {
    case 'INSERT':
      return 'success';
    case 'DELETE':
      return 'error';
    default:
      return 'info';
  }
};

const formatChangeSummary = (activity: EntityActivity): string => {
  if (
    activity.change_summary === 'Moved' &&
    (activity.from_location_name || activity.to_location_name)
  ) {
    const from = activity.from_location_name || 'Unknown';
    const to = activity.to_location_name || 'Unknown';
    return `Moved: ${from} → ${to}`;
  }
  if (activity.receipt_quantity && activity.receipt_quantity > 1) {
    return `${activity.change_summary} (×${activity.receipt_quantity})`;
  }
  return activity.change_summary;
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const EntityActivityList: React.FC<EntityActivityListProps> = ({
  entityType,
  pollingInterval = 5000,
  pageSize = 25,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const { individualIdPrefix } = usePersonLabels();
  const [filterText, setFilterText] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs().subtract(30, 'day'));
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs());

  // Debounce filter text to avoid excessive API calls
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedFilter(event.target.value);
      setPage(0);
    }, 300);
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['entity-activity-list', entityType, page, rowsPerPage, debouncedFilter, dateFrom?.format('YYYY-MM-DD'), dateTo?.format('YYYY-MM-DD')],
    queryFn: async (): Promise<{ items: EntityActivity[]; totalCount: number }> => {
      const params = new URLSearchParams();
      params.append('limit', String(rowsPerPage));
      params.append('offset', String(page * rowsPerPage));
      params.append('order', 'audit_timestamp.desc');

      if (entityType) {
        params.append('entity_type', `eq.${entityType}`);
      }

      if (dateFrom?.isValid()) {
        params.append('audit_timestamp', `gte.${dateFrom.startOf('day').toISOString()}`);
      }
      if (dateTo?.isValid()) {
        params.append('audit_timestamp', `lte.${dateTo.endOf('day').toISOString()}`);
      }

      if (debouncedFilter.trim()) {
        const search = `*${debouncedFilter.trim()}*`;
        params.append('or', `(entity_name.ilike.${search},entity_identifier.ilike.${search})`);
      }

      const endpoint = `/api/v1/entity_activity?${params.toString()}`;
      const { data: items, count } = await httpClient.getWithCount<EntityActivity>(endpoint);
      return { items, totalCount: count };
    },
    refetchInterval: (query) => query.state.status === 'pending' ? false : pollingInterval,
    placeholderData: keepPreviousData,
  });

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const rows = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  // useTableSort must be called unconditionally before any early returns to satisfy the Rules of Hooks
  const { sortField, sortDirection, onSort, sortedRows: sortedData } = useTableSort(
    rows as unknown as Record<string, unknown>[],
  );

  if (isLoading) {
    return (
      <Box>
        {[...Array(10)].map((_, i) => (
          <Skeleton key={i} height={53} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        Failed to load activity history
      </Alert>
    );
  }

  if (!rows.length && !debouncedFilter && !dateFrom && !dateTo && !isLoading) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No activity recorded yet
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-end' }} sx={{ mb: 2, pt: 1, flexShrink: 0 }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Filter by item name or identifier..."
            value={filterText}
            onChange={handleFilterChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 300 }}
          />
          <DatePicker
            label="From"
            value={dateFrom}
            onChange={(value) => { setDateFrom(value?.isValid() ? value : null); setPage(0); }}
            slotProps={{ textField: { size: 'small', InputLabelProps: { shrink: true }, sx: { minWidth: 170 } } }}
          />
          <DatePicker
            label="To"
            value={dateTo}
            onChange={(value) => { setDateTo(value?.isValid() ? value : null); setPage(0); }}
            slotProps={{ textField: { size: 'small', InputLabelProps: { shrink: true }, sx: { minWidth: 170 } } }}
          />
        </Stack>
      </LocalizationProvider>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No matching activity found
        </Typography>
      ) : (
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <SortableTableCell field="entity_name" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Item</SortableTableCell>
                <SortableTableCell field="change_summary" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Action</SortableTableCell>
                <SortableTableCell field="user_name" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>User</SortableTableCell>
                <SortableTableCell field="audit_timestamp" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Time</SortableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(sortedData as unknown as EntityActivity[]).map((activity) => (
                <TableRow key={activity.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {activity.entity_name || (activity.entity_identifier?.replace(/^INDV-/, individualIdPrefix)) || 'Unknown'}
                    </Typography>
                    {activity.entity_identifier && activity.entity_name && (
                      <Typography variant="caption" color="text.secondary">
                        {activity.entity_identifier.replace(/^INDV-/, individualIdPrefix)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatChangeSummary(activity)}
                      size="small"
                      color={getOperationColor(activity.operation)}
                      variant="outlined"
                      sx={{ height: 'auto', fontSize: '0.75rem', py: 0.5 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {activity.user_name || activity.user_email || 'System'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatTimestamp(activity.audit_timestamp)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
};
